import { ConfirmationDialogInitialData } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	useCallback,
	useContext,
	useEffect,
	useMemo
} from "react";
import { HubDialogContext } from "./common";

export type ViewSpecificHandlersOptions = {
	isEditingMobileComment: boolean;
	openConfirmationDialog: (props: ConfirmationDialogInitialData) => Promise<TryResult<void>>;
	onBack?: () => void;
};

export function useViewSpecificHandlers(
	options: ViewSpecificHandlersOptions
): void {
	const {
		isEditingMobileComment,
		openConfirmationDialog,
		onBack
	} = options;

	const { setViewSpecificHandlers } = useContext(HubDialogContext);

	const promptUserBeforeQuittingThePage = useCallback(
		async (): Promise<boolean> => {
			const result: TryResult<void> = await openConfirmationDialog({
				title: localize("_AreYouSureYouWantToLeaveThisPage"),
				children: localize("_YouHaveStartedWritingOrEditingAComment"),
				confirmText: localize("_Stay"),
				cancelText: localize("_Leave"),
				trackingId: "discard-hub-comment-form-confirmation-dialog"
			});

			if (result.wasSuccessful) {
				return true;
			}

			return false;
		},
		[openConfirmationDialog]
	);

	const guardAgainstUnsavedComment = useCallback(
		async (handler: () => void): Promise<void> => {
			if (isEditingMobileComment) {
				const shouldStay: boolean = await promptUserBeforeQuittingThePage();
				if (shouldStay) {
					return;
				}
			}

			handler();
		},
		[isEditingMobileComment, promptUserBeforeQuittingThePage]
	);

	const backHandler = useMemo(
		(): (() => void) | undefined => {
			if (!onBack) {
				return undefined;
			}

			return (): void => {
				guardAgainstUnsavedComment(
					() => onBack()
				);
			};
		},
		[
			guardAgainstUnsavedComment,
			onBack
		]
	);

	useEffect(
		(): (() => void) => {
			const beforeLeavingDetailsHandler: (() => Promise<boolean>) | undefined = !isEditingMobileComment
				? undefined
				: promptUserBeforeQuittingThePage;

			setViewSpecificHandlers({
				backHandler,
				beforeCloseHandler: beforeLeavingDetailsHandler,
				beforeSwitchingCategoryHandler: beforeLeavingDetailsHandler
			});

			return () => {
				setViewSpecificHandlers(undefined);
			};
		},
		[
			backHandler,
			isEditingMobileComment,
			promptUserBeforeQuittingThePage,
			setViewSpecificHandlers
		]
	);
}